import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { FormWorkflowConfig } from "@colab/shared";
import type { AdminFormRow } from "./types";

export type UpdateFormWorkflowInput = {
  id: string;
  workflow: FormWorkflowConfig;
};

type Context = {
  previousDetail: AdminFormRow | undefined;
};

// PATCH /api/admin/forms/:id/workflow
//
// Workflow controls downstream object kind + auto-accept + mapping. Pattern C
// state-flip on the detail cache; list cache is unaffected.
export function useUpdateFormWorkflow() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true },
    ApiClientError,
    UpdateFormWorkflowInput,
    Context
  >({
    mutationFn: ({ id, workflow }) =>
      apiFetch<{ ok: true }>(`/admin/forms/${id}/workflow`, {
        method: "PATCH",
        body: JSON.stringify(workflow),
      }),
    onMutate: async ({ id, workflow }) => {
      const detailKey = queryKeys.adminForms.detail(id);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) =>
        old ? { ...old, workflow } : old,
      );
      return { previousDetail };
    },
    onError: (err, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(queryKeys.adminForms.detail(id), context.previousDetail);
      }
      toast.error(err.message ?? "Failed to update workflow");
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminForms.detail(id) });
    },
  });
}
